import React, { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
    ArrowLeft, ChevronDown, CheckCircle,
    MonitorPlay, Paperclip, ExternalLink,
    Users, Target, GraduationCap, FileText, BookOpen,
    CalendarDays, Clock, ListChecks, Timer, User, PlayCircle
} from "lucide-react"
import {
    getAppCourseDetail,
    getAppCourseContents,
    getAppLessons,
    fetchAppMe,
    getAppContentDetail,
    getLearnerContentTests,
    startLearnerRun
} from "@/api/kuizy"
import { useKuizyTheme } from "@/styles/kuizyTheme"
import { API_BASE } from "@/settings/master"
import LearnerLayout from "@/layout/LearnerLayout"

const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } }
const pop = { initial: { opacity: 0, scale: .96 }, animate: { opacity: 1, scale: 1 }, transition: { duration: .22 } }

export default function LearnerContentDetailPage() {
    const { courseId, contentId } = useParams()
    const nav = useNavigate()
    const { t, theme } = useKuizyTheme()

    // Course Data State (for banner context if you want to keep it simple, or just use content details)
    const [course, setCourse] = useState(null)
    const [content, setContent] = useState(null)
    const [detailData, setDetailData] = useState(null)
    const [lessons, setLessons] = useState([])
    const [exams, setExams] = useState([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('detail')

    // User profile (for LearnerLayout)
    const [profile, setProfile] = useState(null)

    // --- Data Fetching: Course & Content ---
    const fetchData = useCallback(async () => {
        setLoading(true)
        try {
            const [cRes, detRes, lesRes, cntRes, upRes] = await Promise.all([
                getAppCourseDetail(courseId),
                getAppContentDetail(contentId),
                getAppLessons({ contentId }),
                getAppCourseContents(courseId),
                getLearnerContentTests(contentId).catch(() => ({ data: [] }))
            ])

            setCourse(cRes.data || {})
            setDetailData(detRes?.data?.data || null)
            const list = Array.isArray(lesRes.data) ? lesRes.data : (lesRes.data?.data || [])
            setLessons(list)

            // Find current content info from course contents
            const cData = Array.isArray(cntRes.data) ? cntRes.data : (cntRes.data?.data || [])
            const currentContent = cData.find(c => c.id === contentId || c.content_id === contentId)
            setContent(currentContent || null)

            // Filter upcoming exams for this content
            const upRows = Array.isArray(upRes?.data?.rows) ? upRes.data.rows : (Array.isArray(upRes?.data) ? upRes.data : [])
            const filtered = upRows.filter(e =>
                e.content_id === contentId || (!e.content_id && e.course_id === courseId)
            )
            filtered.sort((a, b) => {
                const ta = a?.test_order == null ? Number.MAX_SAFE_INTEGER : Number(a.test_order)
                const tb = b?.test_order == null ? Number.MAX_SAFE_INTEGER : Number(b.test_order)
                return ta - tb
            })
            setExams(filtered)

        } catch (error) {
            console.error("Failed to load content detail data", error)
        } finally {
            setLoading(false)
        }
    }, [courseId, contentId])

    useEffect(() => {
        fetchData()
        window.scrollTo(0, 0)
    }, [fetchData])

    // --- Data Fetching: User & Org ---
    useEffect(() => {
        let ignore = false;
        (async () => {
            try {
                const me = await fetchAppMe();
                if (ignore) return;
                setProfile(me.data || {})
            }
            catch { if (!ignore) nav("/learn/login", { replace: true }); }
        })();
        return () => { ignore = true; };
    }, [nav]);

    // --- Renders ---

    if (loading && !detailData) {
        return (
            <div className={`min-h-screen grid place-items-center ${t.bg}`}>
                <div className="animate-pulse flex flex-col items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-primary/20" />
                </div>
            </div>
        )
    }

    if (!detailData && !loading) {
        return (
            <div className={`min-h-screen grid place-items-center ${t.bg}`}>
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-700">ไม่พบเนื้อหา</h2>
                    <button onClick={() => nav(`/learn/course/${courseId}`)} className="mt-4 text-primary hover:underline">กลับ</button>
                </div>
            </div>
        )
    }

    const avatarUrl = profile?.avatar_url
        ? (/^(http|data:)/.test(profile.avatar_url) ? profile.avatar_url : `${API_BASE}${profile.avatar_url}`)
        : "";
    const avatarText = (profile?.display_name || profile?.name || profile?.username || "?").slice(0, 1).toUpperCase();
    const avatarNode = avatarUrl
        ? <img src={avatarUrl} alt="me" className="w-8 h-8 rounded-full object-cover" />
        : <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary text-white text-sm font-bold">{avatarText}</span>;

    return (
        <LearnerLayout profile={profile} onProfileSaved={() => fetchAppMe().then(r => setProfile(r.data || {})).catch(() => { })}>
            <div className={`min-h-screen pb-20 ${t.bg}`}>

                <main className="max-w-6xl mx-auto px-4 pt-6">

                    {/* Breadcrumb / Back */}
                    <div className="mb-4">
                        <button onClick={() => nav(`/learn/course/${courseId}`)} className={`flex items-center gap-2 text-sm hover:underline ${t.subtext}`}>
                            <ArrowLeft size={16} /> กลับหน้าเนื้อหาหลักสูตร
                        </button>
                    </div>

                    {/* Hero / Banner */}
                    <motion.div {...fadeIn} className="relative rounded-2xl overflow-hidden shadow-lg mb-8 aspect-[21/9] md:aspect-[3/1]">
                        <img src={content?.logo_url} alt={content?.name} className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                            <div className="flex items-center gap-2 text-sm md:text-base font-medium text-pink-300 mb-2">
                                <span className="bg-white/20 backdrop-blur px-2 py-0.5 rounded text-xs text-white">{content?.code || content?.content_code}</span>
                            </div>
                            <h1 className="text-2xl md:text-4xl font-bold mb-3">{content?.name || content?.content_name || 'ชื่อเนื้อหา'}</h1>

                            <div className="flex flex-wrap gap-4 mt-6 text-sm text-gray-300">
                                <div className="flex items-center gap-1.5">
                                    <BookOpen size={16} className="text-pink-400" />
                                    <span>{lessons.length} บทเรียนย่อย</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Tab Bar */}
                    <div className={`flex items-center rounded-xl p-1 mb-8 ${theme === 'dark' ? 'bg-white/10' : 'bg-gray-100'}`}>
                        {[
                            { key: 'detail', label: 'รายละเอียด', icon: BookOpen },
                            { key: 'lessons', label: 'บทเรียน', icon: MonitorPlay },
                            { key: 'exam', label: 'ชุดข้อสอบ', icon: CalendarDays },
                        ].map(tab => {
                            const Icon = tab.icon
                            const isActive = activeTab === tab.key
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive
                                        ? 'bg-primary text-white shadow-sm'
                                        : theme === 'dark' ? 'text-white/70 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    <Icon size={16} />
                                    <span>{tab.label}</span>
                                    {tab.key === 'exam' && exams.length > 0 && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'
                                            }`}>{exams.length}</span>
                                    )}
                                </button>
                            )
                        })}
                    </div>

                    {/* Tab Content */}
                    <div className="mb-12">
                        {activeTab === 'detail' && (
                            <div className="space-y-10">
                                {/* Description */}
                                <section>
                                    <div className="flex items-center gap-2 mb-4">
                                        <BookOpen className="w-5 h-5 text-pink-500" />
                                        <h3 className={`text-lg font-semibold ${t.text}`}>คำอธิบายรายวิชา</h3>
                                    </div>
                                    <div className={`pl-7 leading-relaxed ${t.subtext} whitespace-pre-line`}>
                                        {detailData?.description || "ไม่มีคำอธิบาย"}
                                    </div>
                                </section>

                                {/* Objectives */}
                                <section>
                                    <div className="flex items-center gap-2 mb-4">
                                        <Target className="w-5 h-5 text-pink-500" />
                                        <h3 className={`text-lg font-semibold ${t.text}`}>วัตถุประสงค์</h3>
                                    </div>
                                    <div className="pl-7">
                                        {detailData?.objectives && detailData.objectives.length > 0 && detailData.objectives[0] !== "" ? (
                                            <ul className={`space-y-2 ${t.subtext}`}>
                                                {detailData.objectives.map((obj, i) => (
                                                    <li key={i} className="flex items-start gap-2">
                                                        <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                                        <span>{obj}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className={`text-sm italic ${t.subtext}`}>ไม่มีข้อมูลวัตถุประสงค์</div>
                                        )}
                                    </div>
                                </section>

                                {/* Target Audience */}
                                <section>
                                    <div className="flex items-center gap-2 mb-4">
                                        <Users className="w-5 h-5 text-pink-500" />
                                        <h3 className={`text-lg font-semibold ${t.text}`}>เนื้อหานี้เหมาะสำหรับ</h3>
                                    </div>
                                    <div className="pl-7">
                                        {detailData?.targets && detailData.targets.length > 0 && detailData.targets[0] !== "" ? (
                                            <ul className={`space-y-2 ${t.subtext}`}>
                                                {detailData.targets.map((target, i) => (
                                                    <li key={i} className="flex items-center gap-2">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-pink-400 shrink-0" />
                                                        <span>{target}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className={`text-sm italic ${t.subtext}`}>ไม่มีข้อมูลกลุ่มเป้าหมาย</div>
                                        )}
                                    </div>
                                </section>

                                {/* Certification */}
                                <section>
                                    <div className="flex items-center gap-2 mb-4">
                                        <GraduationCap className="w-5 h-5 text-pink-500" />
                                        <h3 className={`text-lg font-semibold ${t.text}`}>การรับวุฒิบัตร</h3>
                                    </div>
                                    <div className="pl-7">
                                        <div className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-100'} ${t.subtext}`}>
                                            {detailData?.completion_percent || detailData?.exam_pass_percent
                                                ? `ผ่านที่เรียนจบ ${detailData?.completion_percent || 0}% ของเนื้อหา และสอบผ่านไม่น้อยกว่า ${detailData?.exam_pass_percent || 0}% จะสามารถรับวุฒิบัตรได้`
                                                : "ผ่านที่เรียนจบ 80% ของเนื้อหา และสอบผ่านไม่น้อยกว่า 80% จะสามารถรับวุฒิบัตรได้"
                                            }
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}

                        {activeTab === 'lessons' && (
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className={`text-xl font-bold ${t.text}`}>บทเรียน</h2>
                                </div>
                                <div className="space-y-4">
                                    {lessons.length === 0 ? (
                                        <div className={`p-8 rounded-2xl text-center border-dashed border-2 ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                                            <div className="text-gray-400 mb-2">ยังไม่มีบทเรียนในเนื้อหานี้</div>
                                        </div>
                                    ) : (
                                        lessons.map((lesson, idx) => (
                                            <LessonItem key={lesson.id || idx} lesson={lesson} t={t} courseId={courseId} />
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'exam' && (
                            <ExamTab
                                exams={exams}
                                t={t}
                                theme={theme}
                                courseId={courseId}
                                nav={nav}
                            />
                        )}
                    </div>

                </main>

            </div>
        </LearnerLayout>
    )
}

function LessonItem({ lesson, t, courseId }) {
    const [isOpen, setIsOpen] = useState(false)
    const attachments = lesson.attachments || []

    return (
        <div className={`rounded-lg border border-gray-100 ${t.bg}`}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50/50 transition-colors"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className="p-1.5 bg-blue-50 text-blue-600 rounded-md shrink-0">
                        <MonitorPlay size={18} />
                    </div>
                    <div className="min-w-0">
                        <div className={`text-sm font-medium truncate ${t.text}`}>{lesson.lesson_name || lesson.name}</div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                            <span>{attachments.length} สื่อการเรียนรู้</span>
                        </div>
                    </div>
                </div>
                {attachments.length > 0 && (
                    <ChevronDown size={16} className={`transform transition-transform text-gray-400 ${isOpen ? 'rotate-180' : ''}`} />
                )}
            </button>

            <AnimatePresence>
                {isOpen && attachments.length > 0 && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        className="overflow-hidden bg-gray-50/50"
                    >
                        <ul className="p-3 space-y-2">
                            {attachments.map((att, i) => (
                                <li key={i}>
                                    <AttachmentLink att={att} t={t} courseId={courseId} />
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

function AttachmentLink({ att, t, courseId }) {
    const isVideo = att.type === "video" || att.type === "mp4" || /\.(mp4|webm|ogg|mov)$/i.test(att.file_name || att.name || att.url || "")
    const isPlayable = isVideo || att.type === "link" || att.type === "youtube" || att.type === "url" || att.type === 'pdf' || att.check_type === 2
    const Icon = isPlayable ? (isVideo ? PlayCircle : ExternalLink) : (att.type === 'pdf' ? FileText : Paperclip)
    const colorClass = isPlayable ? (isVideo ? "text-purple-500 bg-purple-50" : "text-green-500 bg-green-50") : "text-orange-500 bg-orange-50"

    const nav = useNavigate()

    const handleClick = (e) => {
        if (isPlayable) {
            e.preventDefault()
            if (att.id || att.attachment_id) {
                nav(`/learn/course/${courseId}/play/${att.id || att.attachment_id}`)
            }
        }
    }

    return (
        <a
            href={att.url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-white border border-transparent hover:border-gray-200 transition-all group"
        >
            <div className={`p-1.5 rounded-md ${colorClass} group-hover:scale-110 transition-transform`}>
                <Icon size={14} />
            </div>
            <span className={`text-sm ${t.text} group-hover:text-primary transition-colors line-clamp-1`}>
                {att.name || att.file_name || "Attachment"}
            </span>
        </a>
    )
}

// --- Helper functions (same as LearnerHomePage) ---
function fmtTimeRange(st, et) {
    try {
        if (!st && !et) return "";
        const a = st instanceof Date ? st : new Date(st);
        const b = et instanceof Date ? et : new Date(et);
        if (a && b) return `${a.toLocaleString()} - ${b.toLocaleString()}`;
        return (a ? a.toLocaleString() : "") || (b ? b.toLocaleString() : "");
    } catch { return ""; }
}

function getStatusMeta(st, et) {
    const now = Date.now();
    const s = st ? (st instanceof Date ? st : new Date(st)).getTime() : null;
    const e = et ? (et instanceof Date ? et : new Date(et)).getTime() : null;
    if (s && now < s) {
        const diff = s - now;
        const m = Math.max(0, Math.floor(diff / 60000));
        const d = Math.floor(m / 1440);
        const h = Math.floor((m % 1440) / 60);
        const mm = m % 60;
        if (d > 0) return { text: `อีก ${d} วัน ${h} ชม`, kind: "soon" };
        if (h > 0) return { text: `อีก ${h} ชม ${mm} นาที`, kind: "soon" };
        return { text: `อีก ${mm} นาที`, kind: "soon" };
    }
    const started = !s || now >= s;
    const notEnded = !e || now <= e;
    if (started && notEnded) return { text: "พร้อม", kind: "ready" };
    return { text: "หมดเวลา", kind: "over" };
}

// --- ExamTab component ---
function ExamTab({ exams, t, theme, courseId, nav }) {
    const isDark = theme === 'dark';
    const readyChip = isDark ? "bg-green-500/20 text-green-300 ring-1 ring-green-500/30" : "bg-green-100 text-green-700 ring-1 ring-green-200";
    const soonChip = isDark ? "bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/30" : "bg-orange-100 text-orange-700 ring-1 ring-orange-200";
    const overChip = isDark ? "bg-gray-500/20 text-gray-300 ring-1 ring-gray-500/30" : "bg-gray-100 text-gray-700 ring-1 ring-gray-200";
    const scoreChip = isDark ? "bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/30" : "bg-blue-100 text-blue-700 ring-1 ring-blue-200";

    const handleStart = async (cId, testId) => {
        try {
            const { data } = await startLearnerRun({ course_id: cId, test_id: testId });
            const rid = data?.run_id || data?.id || data?.test_run_id;
            if (rid) nav(`/learn/exam/${rid}`);
            else nav(`/learn/exam?course_id=${cId}&test_id=${testId}`);
        } catch { }
    };

    if (exams.length === 0) {
        return (
            <div className={`p-8 rounded-2xl text-center border-dashed border-2 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                <CalendarDays className="mx-auto mb-3 text-gray-300" size={40} />
                <div className="text-gray-400 mb-1">ยังไม่มีชุดข้อสอบในเนื้อหานี้</div>
                <div className={`text-xs ${t.subtext}`}>เมื่อมีชุดข้อสอบที่พร้อมสอบจะแสดงที่นี่</div>
            </div>
        )
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className={`text-xl font-bold ${t.text}`}>ชุดข้อสอบ</h2>
                <span className={`text-sm ${t.subtext}`}>{exams.length} รายการ</span>
            </div>
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {exams.map(it => {
                    const st = it?.start_time ? new Date(it.start_time) : null;
                    const et = it?.end_time ? new Date(it.end_time) : null;
                    const meta = getStatusMeta(st, et);
                    const chipCls = meta.kind === "ready" ? readyChip : meta.kind === "soon" ? soonChip : overChip;
                    const qn = it?.question_count ?? it?.total_questions ?? it?.num_questions;
                    const dur = it?.duration_minutes ?? it?.duration ?? it?.limit_min;
                    const img = it?.image_url || it?.logo_url || it?.thumbnail_url || it?.cover_url;
                    const alreadySubmitted = !!it?.submission_id;
                    const scoreText = (it?.score != null && it?.full_score != null) ? `คะแนน ${it.score}/${it.full_score}` : null;

                    return (
                        <li
                            key={it.course_test_id || `${it.course_id}-${it.test_id}-${it.test_order || ""}`}
                            className={`group rounded-2xl flex flex-col gap-4 p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${isDark ? "bg-white/5 border border-white/10" : "bg-white border border-gray-100 shadow-sm"}`}
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h4 className={`font-semibold line-clamp-2 min-h-[44px] leading-snug ${isDark ? "text-white" : "text-gray-800"}`}>
                                        {it?.test_name}
                                    </h4>
                                    <div className={`mt-1.5 text-xs truncate ${t.subtext}`}>
                                        {it?.course_name}
                                    </div>
                                </div>
                                <div className={`shrink-0 text-[11px] px-2.5 py-1 rounded-full font-medium ${chipCls}`}>
                                    {meta.text}
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-1">
                                {qn ? (
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-50 text-blue-500 shrink-0">
                                            <ListChecks size={18} />
                                        </div>
                                        <div>
                                            <div className={`font-semibold text-sm ${isDark ? "text-white" : "text-gray-800"}`}>{qn}</div>
                                            <div className="text-gray-500 text-[10px]">ข้อ</div>
                                        </div>
                                    </div>
                                ) : <div />}

                                {dur ? (
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-orange-50 text-orange-500 shrink-0">
                                            <Timer size={18} />
                                        </div>
                                        <div>
                                            <div className={`font-semibold text-sm ${isDark ? "text-white" : "text-gray-800"}`}>{dur}</div>
                                            <div className="text-gray-500 text-[10px]">นาที</div>
                                        </div>
                                    </div>
                                ) : <div />}
                            </div>

                            {/* Time */}
                            {(st || et) ? (
                                <div className={`flex items-start gap-2 text-xs p-3 rounded-xl ${isDark ? "bg-black/20 text-gray-300" : "bg-gray-50 text-gray-600"}`}>
                                    <Clock className="size-4 opacity-50 shrink-0 mt-0.5" />
                                    <div className="leading-relaxed">
                                        {fmtTimeRange(st, et)}
                                    </div>
                                </div>
                            ) : null}

                            {/* Buttons */}
                            <div className="pt-2 mt-auto">
                                {!alreadySubmitted && meta.kind === "ready" ? (
                                    <button
                                        onClick={() => handleStart(it.course_id, it.test_id)}
                                        className="w-full h-11 rounded-full text-sm font-semibold bg-primary text-white hover:bg-secondary transition-colors cursor-pointer flex items-center justify-center gap-2"
                                    >
                                        <PlayCircle size={18} />
                                        เริ่มสอบ
                                    </button>
                                ) : null}
                                {alreadySubmitted && scoreText ? (
                                    <button
                                        className={`w-full h-11 rounded-full text-sm font-semibold cursor-pointer transition-opacity hover:opacity-80 flex items-center justify-center ${scoreChip}`}
                                        onClick={() => {
                                            const qs = new URLSearchParams({
                                                sid: String(it.submission_id || ""),
                                                score: String(it.score ?? 0),
                                                full: String(it.full_score ?? 0),
                                                spend: String(it.spend_sec ?? it.spend ?? 0)
                                            }).toString();
                                            nav(`/learn/exam/result/${it.test_run_id}?${qs}`);
                                        }}
                                    >
                                        ดูผลการสอบ ({scoreText})
                                    </button>
                                ) : null}
                                {!alreadySubmitted && meta.kind !== "ready" ? (
                                    <div className={`w-full h-11 rounded-full text-sm font-semibold flex items-center justify-center ${overChip}`}>
                                        {meta.kind === "soon" ? "ยังไม่ถึงเวลา" : "หมดเวลา"}
                                    </div>
                                ) : null}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    )
}
